import { ArrowUpRight } from "lucide-react";
import { SITE } from "@/config";
import { Chapter, Reveal } from "@/components/Reveal";
import { GhostCTA } from "@/components/CTA";

const AUDIENCE = [
  "Imprenditori e imprenditrici del settore beauty",
  "Titolari di centri estetici, saloni e attività del benessere",
  "Chi vuole far crescere la propria attività senza restare intrappolato nell'operatività quotidiana",
];

const IMPROVES = [
  "Uscita progressiva dall'operatività",
  "Organizzazione e gestione dell'attività",
  "Capacità di trasformare il lavoro in guadagno",
];

const ProjectsPage = () => (
  <div data-testid="projects-page" className="bg-ink pt-[72px] text-white">
    <section className="relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(212,175,55,0.08),transparent_55%)]"
      />
      <div className="relative mx-auto max-w-[1440px] px-6 py-20 lg:px-12 lg:py-28">
        <Chapter index="—" label="Progetti" dark />
        <Reveal>
          <h1 className="font-display text-5xl font-extrabold tracking-tighter sm:text-6xl lg:text-7xl">
            Progetti<span className="text-accent">.</span>
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60 md:text-lg">
            Strumenti concreti nati dal lavoro sul campo: la consulenza che diventa metodo, e il
            metodo che diventa strumento per gli imprenditori.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="mt-16">
          <div className="gold-glow-hover max-w-2xl border border-white/10 bg-[#0C0C0C] p-8 lg:p-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
              Progetto editoriale
            </p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
              BEAUTY TASK
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-white/55 md:text-base">
              La guida semplice per imprenditori e imprenditrici nel beauty che vogliono guadagnare
              e uscire dall'operatività quanto prima.
            </p>
            <a
              href="#beauty-task"
              data-testid="book-cta-scopri"
              className="group mt-8 inline-flex items-center gap-3 border border-accent/40 px-7 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-accent transition-colors duration-300 hover:border-accent hover:bg-accent/10"
            >
              Scopri Beauty Task
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>

    <section id="beauty-task" className="border-t border-white/10">
      <div className="mx-auto grid max-w-[1440px] gap-14 px-6 py-20 lg:grid-cols-12 lg:px-12 lg:py-28">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="relative max-w-[360px]">
              <div className="absolute -left-3 -top-3 h-full w-full border border-accent/25" aria-hidden="true" />
              <img
                src="/assets/beauty-task-cover.webp"
                alt="Beauty Task — copertina del libro di Mattia Cosimo Saracino"
                data-testid="book-cover"
                className="relative h-auto w-full"
                style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.65), 0 0 60px rgba(212,175,55,0.10)" }}
              />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent">
              Beauty Task — Il libro
            </p>
            <h2 className="mt-4 font-display text-3xl font-extrabold leading-[1.05] tracking-tighter sm:text-4xl">
              La consulenza beauty che diventa metodo.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60">
              Beauty Task è il progetto editoriale di Mattia Cosimo Saracino: l'espressione concreta
              della sua esperienza nella consulenza aziendale applicata al settore beauty, dove
              persone, organizzazione e numeri fanno la differenza ogni giorno.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-10 sm:grid-cols-2">
            <Reveal delay={0.1}>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">A chi è rivolto</p>
              <ul className="mt-5 space-y-3">
                {AUDIENCE.map((a) => (
                  <li key={a} className="flex items-baseline gap-3 text-sm leading-relaxed text-white/60">
                    <span className="text-accent" aria-hidden="true">—</span>
                    {a}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
                Cosa aiuta a migliorare
              </p>
              <ul className="mt-5 space-y-3">
                {IMPROVES.map((a) => (
                  <li key={a} className="flex items-baseline gap-3 text-sm leading-relaxed text-white/60">
                    <span className="text-accent" aria-hidden="true">—</span>
                    {a}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="mt-12 flex flex-wrap items-center gap-4">
            <a
              href={SITE.amazonBeautyTask}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-cta-amazon"
              className="btn-gold group inline-flex items-center gap-3 px-7 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.22em]"
            >
              Acquista Beauty Task su Amazon
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
            <GhostCTA to="/contatti" dark testid="book-cta-info">
              Richiedi informazioni
            </GhostCTA>
          </Reveal>
        </div>
      </div>
    </section>
  </div>
);

export default ProjectsPage;
